import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import { createReducer } from '@reduxjs/toolkit';
import * as actions from 'store/findProvider/actions';
import { Nullable } from 'modules/types/common';
import { START_PAGE_NUMBER } from '../constants';
import * as types from '../types';
import { updatedProvidersWithOrderedVisitTypes } from '../utils';

export const searchResultInitialState = {
  isFetching: false,
  isHydrating: false,
  providers: [] as types.ProviderSummary[],
  totalRequested: 0,
  totalResults: 0,
  rawProviderCount: 0,
  rawProviderTotal: 0,
  notes: [] as types.SearchResultNote[],
  pageNumber: 0,
  error: null as Nullable<Error>
};

export const newSearchResultInitialState = {
  isFetching: false,
  isHydrating: false,
  results: [],
  pageNumber: 0,
  error: null as Nullable<Error>
};

export type SearchResultState = typeof searchResultInitialState;

const searchResultReducer = createReducer(searchResultInitialState, {
  [actions.PROVIDER_SEARCH]: state => {
    state.isFetching = true;
    state.error = null;
  },
  [actions.PROVIDER_SEARCH_MORE]: state => {
    state.isHydrating = true;
    state.error = null;
  },
  [actions.PROVIDER_SEARCH_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.pageNumber = START_PAGE_NUMBER;

    /*
      this warrants some explanation:
      search results are technically provider-location combinations. Each "provider result" is one of it's location combinations
      when we execute a "search" we could get duplicate providers (due to multiple locations being returned in the same set)
      to deal with this, we keep track of both:
        a) the absolute total # of results we're getting back, unfiltered (contains duplicates)
        b) the same result set, but filtered for uniqueness on providerId so that a provider is only in the result set once

      However, in order to properly handle pagination, we need to keep track of each the filtered/non-filtered result sets, and the COUNT of those sets as well
      Once rawProviderCount == rawProviderTotal, we have arrived at the "end" of the pages
    */
    const rawProviders: types.ProviderSummary[] = get(action, 'payload.data.results') || [];
    state.rawProviderTotal = get(action, 'payload.data.totalResults') || 0;
    state.rawProviderCount = rawProviders.length;
    state.totalRequested = rawProviders.length;
    state.providers = uniqBy(updatedProvidersWithOrderedVisitTypes(rawProviders), 'providerId');
    state.totalResults = state.providers.length;
  },
  [actions.PROVIDER_SEARCH_MORE_SUCCESS]: (state, action) => {
    state.isHydrating = false;
    state.pageNumber += 1;
    state.providers = uniqBy(
      updatedProvidersWithOrderedVisitTypes(
        state.providers.concat(get(action, 'payload.data.results') || [])
      ),
      'providerId'
    );
    state.rawProviderCount = get(action, 'payload.data.results.length', 0);
    state.totalResults = state.providers.length;
    state.totalRequested += get(action, 'payload.data.results.length', 0);
  },
  [actions.PROVIDER_SEARCH_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = get(action, 'error');
  },
  [actions.PROVIDER_SEARCH_MORE_FAIL]: (state, action) => {
    state.isHydrating = false;
    state.error = action.error;
  },
  [actions.PROVIDER_SEARCH_RESET_RESULTS]: state => {
    state.providers = [];
    state.totalResults = searchResultInitialState.totalResults;
  }
});

export default searchResultReducer;
